import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Tabs } from 'src/design/molecules/Tabs';
import { useParams, useNavigate } from 'react-router-dom';
import { RfiDto, RfiHistoryDto } from 'src/api/FiretrolApiClient';
import { useRfi } from 'src/api/queries/rfis/useRfiQueries';
import { useJobs } from 'src/hooks/useJobs';
import {
  RFIForm,
  RFIFormSection,
} from 'src/routes/Jobs/RFIs/components/RFIForm';
import { NotificationsTab } from 'src/app/workflows/NotificationsTab';
import { HistoryList } from 'src/app/workflows/HistoryList';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Email } from 'src/app/workflows/Email';

export const ViewRFI: React.FC = () => {
  const params = useParams<{ jobId: string; rfiId: string }>();
  const navigate = useNavigate();
  const { selectedJob, users } = useJobs();
  const [tab, setTab] = React.useState<RFIFormSection>('details');
  const {
    data: rfi,
    isLoading: loading,
    isError,
  } = useRfi(selectedJob?.id, params.rfiId);
  const rfiHistory: RfiHistoryDto[] = rfi?.history ?? [];

  if (loading) return <Loader />;
  if (isError) return <p>Unable to find RFI</p>;
  if (!rfi || !selectedJob) return <p>Unable to find RFI</p>;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(
            selectedJob,
            { text: 'RFIs', href: `/jobs/${selectedJob.id}/rfis` },
            rfi.rfiNumber || 'No RFI Number',
          ),
        }}
        title={rfi.rfiNumber || 'View RFI'}
        helpText="View and update this RFI's details, attachments, and notifications."
        actions={
          <SecondaryButton
            onClick={() => navigate(`/jobs/${selectedJob.id}/rfis`)}
          >
            Cancel
          </SecondaryButton>
        }
      />

      <Tabs
        value={tab}
        onChange={(value) => setTab((value as RFIFormSection) || 'details')}
        mt="md"
      >
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="attachments">Attachments</Tabs.Tab>
          <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
          <Tabs.Tab value="email">Email</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Box mt="md">
        <RFIForm
          initialValues={rfi}
          selectedJob={selectedJob}
          activeSection={tab}
          jobUsers={users ?? []}
        >
          {tab === 'notifications' && (
            <NotificationsTab
              resourceType="rfi"
              entityNoun="RFI"
              attachPdfLabel="Attach RFI PDF"
              jobId={selectedJob.id}
              entityId={rfi.id}
              jobUsers={users ?? []}
              customerNumber={selectedJob.customerNumber?.trim() ?? ''}
            />
          )}

          {tab === 'history' && <HistoryList history={rfiHistory} />}

          {tab === 'email' && (
            <Email
              jobNumber={selectedJob.jobNumber}
              productType="RFI"
              productNumber={rfi.rfiNumber ?? ''}
            />
          )}
        </RFIForm>
      </Box>
    </>
  );
};
